import { type DiffStrategy, type DiffTreeChange, diffTrees } from '@gitbook/util-tree-merge';

import { compareTags } from '../tags';
import type { Revision, RevisionTag, RevisionTags } from '../types';

/** Tag from a revision, as a tree data structure */
export interface RevisionTreeTag {
    tag: RevisionTag;
    diff?: 'added' | 'deleted' | 'modified';
}

/** Result when diffing revisions tags */
export interface TagsDiffResult {
    tags: RevisionTreeTag[];
    diffs: Map<string, DiffTreeChange<RevisionTreeTag>>;
}

/**
 * Diff two revisions and return a set of tags.
 */
export function diffRevisionTags(
    current: Revision,
    parent: Revision,
    options: {
        /**
         * If false, deleted tags will not be included in the result.
         */
        includeDeleted?: boolean;
    } = {}
): TagsDiffResult {
    const { includeDeleted = true } = options;

    const diffStrategy: DiffStrategy<RevisionTreeTag> = {
        getId: (node) => node.tag.slug,
        getChildren: () => [],
        setChildren: (node) => ({ ...node }),
        compareItems: (original, modified) => (compareTags(original.tag, modified.tag) ? 0 : 1),
        annotateChange: (node, diff) => {
            if (!includeDeleted && diff.type === 'deleted') {
                return [];
            }
            return [
                {
                    ...node,
                    diff: diff.type,
                },
            ];
        },
    };

    const result = diffTrees(
        {
            before: createTreeTags(parent.tags),
            after: createTreeTags(current.tags),
        },
        diffStrategy
    );

    return {
        tags: result.items,
        diffs: result.changes,
    };
}

/**
 * Create a tree from a set of tags.
 */
export function createTreeTags(tags: RevisionTags | undefined): RevisionTreeTag[] {
    if (!tags) {
        return [];
    }
    return Object.values(tags)
        .map((tag) => ({ tag }))
        .sort((a, b) => a.tag.slug.localeCompare(b.tag.slug));
}
